import { useCallback, useEffect, useState } from "react";
import { useAxiosContext } from "../context/AxiosContext";
import { useAppOrgContext } from "../context/AppOrgContext";

const THEME_STYLE_ID = "z-theme-style";
const DEVICE_STYLE_ID = "z-device-style";
const MOBILE_CSS = "/MobileMain.css";
const DESKTOP_CSS = "/main.css";
const TABLET_CSS = "/TabMain.css";

const themeCache = {
  id: null,
  desktop_theme_id: null,
  mobile_theme_id: null,
  tablet_theme_id: null,
  css: {
    desktop: "",
    mobile: "",
    tablet: ""
  }
};

const injectDeviceCSS = (device) => {
  // const href = device === "mobile" ? MOBILE_CSS : DESKTOP_CSS;
  let href;
  switch (device) {
    case "mobile":
      href = MOBILE_CSS;
      break;
    case "desktop":
      href = DESKTOP_CSS;
      break;
    case "tablet":
      href = TABLET_CSS;
      break;
  }
  let link = document.getElementById(DEVICE_STYLE_ID);

  if (link) {
    // Only update if href has changed
    if (link.href !== href) {
      link.href = href;
    }
  } else {
    link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.id = DEVICE_STYLE_ID;
    link.href = href;
    document.head.appendChild(link);
  }
};

const injectThemeCSS = (css) => {
  let style = document.getElementById(THEME_STYLE_ID);

  if (style) {
    if (style.textContent !== css) {
      style.textContent = css;
    }
  } else {
    style = document.createElement("style");
    style.id = THEME_STYLE_ID;
    style.textContent = css;
    document.head.appendChild(style);
  }
};

const useZTheme = (device) => {
  const [themeCSS, setThemeCSS] = useState("");
  const { axiosInstance } = useAxiosContext();
  const { application } = useAppOrgContext();

  const fetchAndApplyTheme = useCallback(
    async (deviceType, themeId, appId, desktopId, mobileId, tabletId) => {
      if (!themeId || !deviceType || !appId) return;

      try {
        const { data } = await axiosInstance.get(`/sysconfigs/${themeId}`);
        const css = data.config.css || "";

        // Update cache fully
        themeCache.id = appId;
        themeCache.desktop_theme_id = desktopId;
        themeCache.mobile_theme_id = mobileId;
        themeCache.tablet_theme_id = tabletId;
        themeCache.css = {
          ...themeCache.css,
          [deviceType]: css
        };

        setThemeCSS(css);
        injectDeviceCSS(deviceType);
        injectThemeCSS(css);
      } catch (error) {
        console.error("Error loading theme:", error);
        setThemeCSS("");
      }
    },
    [axiosInstance]
  );

  useEffect(() => {
    if (!device || !application?.id) return;

    const appId = application.id;
    const desktopThemeId = application.desktop_theme_id;
    const mobileThemeId = application.mobile_theme_id;
    const tabletThemeId = application.tablet_theme_id;

    // const themeId = device === "desktop" ? desktopThemeId : mobileThemeId;
    let themeId;
    switch (device) {
      case "desktop":
        themeId = desktopThemeId;
        break;
      case "mobile":
        themeId = mobileThemeId;
        break;
      case "tablet":
        themeId = tabletThemeId;
        break;
    }

    const isSameApp =
      themeCache.id === appId &&
      themeCache.desktop_theme_id === desktopThemeId &&
      themeCache.mobile_theme_id === mobileThemeId &&
      themeCache.tablet_theme_id === tabletThemeId;

    const hasCachedCSS = themeCache.css[device];

    if (isSameApp && hasCachedCSS) {
      setThemeCSS(themeCache.css[device]);
      injectDeviceCSS(device);
      injectThemeCSS(themeCache.css[device]);
    } else {
      fetchAndApplyTheme(device, themeId, appId, desktopThemeId, mobileThemeId, tabletThemeId);
    }
  }, [
    device,
    application?.id,
    application?.desktop_theme_id,
    application?.mobile_theme_id,
    application?.tablet_theme_id,
    fetchAndApplyTheme
  ]);

  return { themeCSS };
};

export default useZTheme;
